// Theme switch (light / dark). Pure module: no React and no imports, so it loads in
// the Node test harness. The preference is per user, per browser:
//   localStorage['jobvious.theme.<User_id>'] = 'light' | 'dark'
// The active theme is the `data-theme` attribute on <html>; tokens.css keys off it.

const KEY_PREFIX = 'jobvious.theme.';
export const THEME_EVENT = 'themeChanged';

// Routes that never go dark: customer-facing pages reached without (or regardless of) login.
export const ALWAYS_LIGHT_PATHS = [
  '/claim', '/referralclaim', '/public/referral', '/quote/',
  '/signup/dealer', '/welcome', '/estimate', '/unsubscribe',
];

const hasId = (userId) => userId !== undefined && userId !== null && userId !== '';
const normalize = (theme) => (theme === 'dark' ? 'dark' : 'light');
const store = () => {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; }
};

export const themeKey = (userId) => `${KEY_PREFIX}${userId}`;

export function isAlwaysLightPath(pathname) {
  return ALWAYS_LIGHT_PATHS.some((p) => String(pathname || '').startsWith(p));
}

export function currentUserId() {
  try {
    const user = JSON.parse(store()?.getItem('userAPI') || 'null');
    return user && hasId(user.User_id) ? user.User_id : null;
  } catch { return null; }
}

export function getTheme(userId) {
  if (!hasId(userId)) return 'light';
  try { return normalize(store()?.getItem(themeKey(userId))); } catch { return 'light'; }
}

// What the page should show right now: the user's saved theme, except on always-light routes.
export function resolveTheme(userId, pathname) {
  return isAlwaysLightPath(pathname) ? 'light' : getTheme(userId);
}

export function applyTheme(theme) {
  const t = normalize(theme);
  if (typeof document !== 'undefined') document.documentElement.setAttribute('data-theme', t);
  return t;
}

export function activeTheme() {
  if (typeof document === 'undefined') return 'light';
  return normalize(document.documentElement.getAttribute('data-theme'));
}

export function setTheme(userId, theme) {
  const t = applyTheme(theme);
  try { if (hasId(userId)) store()?.setItem(themeKey(userId), t); } catch { /* still applied for this session */ }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { theme: t } }));
  return t;
}

// Sign-out and sign-in wipe localStorage. This does the same wipe but keeps every
// user's theme choice, and puts the page back to light for the login screen.
export function clearSessionKeepingTheme() {
  const s = store();
  if (s) {
    const kept = [];
    try {
      for (let i = 0; i < s.length; i++) {
        const k = s.key(i);
        if (k && k.startsWith(KEY_PREFIX)) kept.push([k, s.getItem(k)]);
      }
    } catch { /* nothing to keep */ }
    s.clear();
    try { kept.forEach(([k, v]) => s.setItem(k, v)); } catch { /* no-op */ }
  }
  applyTheme('light');
}

// Resolved value of a token for code that cannot take var(): chart props, canvas.
export function themeColor(token, fallback = '') {
  if (typeof document === 'undefined' || typeof getComputedStyle === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(token).trim() || fallback;
}

// Google Static Maps "night" style, as URL parameters. Static map images are fetched
// from Google, so they cannot read CSS tokens; the style has to go in the request.
// Colours use the API's own 0xRRGGBB form.
const STATIC_MAP_NIGHT_STYLE = [
  'element:geometry|color:0x242f3e',
  'element:labels.text.stroke|color:0x242f3e',
  'element:labels.text.fill|color:0x746855',
  'feature:administrative.locality|element:labels.text.fill|color:0xd59563',
  'feature:poi|element:labels.text.fill|color:0xd59563',
  'feature:poi.park|element:geometry|color:0x263c3f',
  'feature:poi.park|element:labels.text.fill|color:0x6b9a76',
  'feature:road|element:geometry|color:0x38414e',
  'feature:road|element:geometry.stroke|color:0x212a37',
  'feature:road|element:labels.text.fill|color:0x9ca5b3',
  'feature:road.highway|element:geometry|color:0x746855',
  'feature:road.highway|element:geometry.stroke|color:0x1f2835',
  'feature:road.highway|element:labels.text.fill|color:0xf3d19c',
  'feature:transit|element:geometry|color:0x2f3948',
  'feature:transit.station|element:labels.text.fill|color:0xd59563',
  'feature:water|element:geometry|color:0x17263c',
  'feature:water|element:labels.text.fill|color:0x515c6d',
  'feature:water|element:labels.text.stroke|color:0x17263c',
];

// Query-string fragment to append to a Static Maps URL: '' in light, the night
// style in dark. Call at render time so a theme toggle refetches the image.
export function staticMapThemeParams(theme = activeTheme()) {
  if (theme !== 'dark') return '';
  return STATIC_MAP_NIGHT_STYLE.map((s) => `&style=${encodeURIComponent(s)}`).join('');
}
